import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ErrorSummaryCard } from "./ImportErrorComponents";
import { EnhancedPackageImportedCard } from "./EnhancedPackageImportedCard";
import { PackageImportedCard } from "./PackageImportedCard";
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import {
  LayoutGrid,
  List,
  Zap,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";

export function EnhancedPackagesImportedList({
  initialOrders,
  useEnhancedView = true,
  onBulkAction,
}) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState(
    useEnhancedView ? "enhanced" : "compact"
  );
  const [selectedPackages, setSelectedPackages] = useState(new Set());

  const hasError = (order, field) => {
    return order.errors.some((error) =>
      error.toLowerCase().includes(field.toLowerCase())
    );
  };

  const handleFixError = (packageId, fixType) => {
    if (fixType === "auto") {
      // Implement auto-fix logic
      console.log(`Auto-fixing package ${packageId}`);
    } else {
      // Implement manual fix logic
      console.log(`Manual fix for package ${packageId}`);
    }
  };

  const handleBulkFix = () => {
    if (onBulkAction) {
      onBulkAction("fix", Array.from(selectedPackages));
    }
  };

  const filteredOrders = useMemo(() => {
    let filtered = initialOrders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.data.trackingNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.data.customer?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.data.customer?.phone?.includes(searchTerm) ||
          order._id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus === "valid") {
      filtered = filtered.filter((order) => order.errors.length === 0);
    } else if (filterStatus === "errors") {
      filtered = filtered.filter((order) => order.errors.length > 0);
    }

    return filtered;
  }, [initialOrders, searchTerm, filterStatus]);

  const statistics = useMemo(() => {
    const total = initialOrders.length;
    const valid = initialOrders.filter(
      (order) => order.errors.length === 0
    ).length;
    const errors = total - valid;

    return { total, valid, errors };
  }, [initialOrders]);

  const handlePackageSelect = (packageId, isSelected) => {
    setSelectedPackages((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(packageId);
      } else {
        newSet.delete(packageId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedPackages.size === filteredOrders.length) {
      setSelectedPackages(new Set());
    } else {
      setSelectedPackages(new Set(filteredOrders.map((order) => order._id)));
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <ErrorSummaryCard
        totalPackages={statistics.total}
        validPackages={statistics.valid}
        errorPackages={statistics.errors}
        onFilterChange={setFilterStatus}
        onSearchChange={setSearchTerm}
        searchTerm={searchTerm}
        filterStatus={filterStatus}
      />

      {/* Bulk Actions & View Toggle */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          {selectedPackages.size > 0 && (
            <Card className="px-3 py-2">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {selectedPackages.size} {t("import.selected")}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkFix}
                  className="h-7"
                >
                  <Zap className="h-3 w-3 mr-1" />
                  {t("import.bulkFix")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSelectAll}
                  className="h-7"
                >
                  {selectedPackages.size === filteredOrders.length
                    ? t("common.deselectAll")
                    : t("common.selectAll")}
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "enhanced" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("enhanced")}
            className="h-8"
          >
            <LayoutGrid className="h-3 w-3 mr-1" />
            {t("import.detailedView")}
          </Button>
          <Button
            variant={viewMode === "compact" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("compact")}
            className="h-8"
          >
            <List className="h-3 w-3 mr-1" />
            {t("import.compactView")}
          </Button>
        </div>
      </div>

      {/* Results Summary */}
      {filteredOrders.length !== initialOrders.length && (
        <Card className="p-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t("import.showingResults", {
                count: filteredOrders.length,
                total: initialOrders.length,
              })}
            </span>
            {filterStatus !== "all" && (
              <Badge variant="outline">
                {filterStatus === "valid" ? (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t("import.validOnly")}
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {t("import.errorsOnly")}
                  </>
                )}
              </Badge>
            )}
          </div>
        </Card>
      )}

      {/* Package List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-muted-foreground">
              {searchTerm || filterStatus !== "all"
                ? t("import.noMatchingPackages")
                : t("import.noPackagesFound")}
            </div>
          </Card>
        ) : (
          filteredOrders.map((order) => (
            <div key={order._id} className="relative">
              {/* Selection Checkbox for Enhanced View */}
              {viewMode === "enhanced" && (
                <div className="absolute -left-6 top-4 z-10">
                  <input
                    type="checkbox"
                    checked={selectedPackages.has(order._id)}
                    onChange={(e) =>
                      handlePackageSelect(order._id, e.target.checked)
                    }
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </div>
              )}

              {viewMode === "enhanced" ? (
                <EnhancedPackageImportedCard
                  initialOrder={order}
                  hasError={hasError}
                  onFixError={handleFixError}
                />
              ) : (
                <PackageImportedCard initialOrder={order} hasError={hasError} />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer Summary */}
      {filteredOrders.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className="text-muted-foreground">
                {t("import.totalShowing")}: {filteredOrders.length}
              </span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>
                    {filteredOrders.filter((o) => o.errors.length === 0).length}{" "}
                    {t("import.valid")}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>
                    {filteredOrders.filter((o) => o.errors.length > 0).length}{" "}
                    {t("import.withErrors")}
                  </span>
                </div>
              </div>
            </div>

            {selectedPackages.size === 0 && (
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                {t("common.selectAll")}
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
